import React, { useState } from "react";

import { Link, useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import {
  ArrowRight,
  Copy,
  Eye,
  FileText,
  Loader2,
  MoreHorizontal,
  Pencil,
  Trash2,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import QuotationStatusBadge from "~/pages/presupuestos/components/QuotationStatusBadge";
import { useQuotations } from "~/pages/presupuestos/hooks/useQuotations";
import { canDeleteQuotation, canEditQuotation } from "~/types/quotation";
import { formatCurrency } from "~/utils/helpers";
import { generateQuotationPdf } from "~/utils/quotationPdf";

const PresupuestosPage: React.FC = () => {
  const navigate = useNavigate();
  const [generatingPdfId, setGeneratingPdfId] = useState<string | null>(null);

  const {
    quotations,
    isLoading,
    handleDelete,
    handleConvertToSalesRequest,
    handleDuplicate,
  } = useQuotations();

  const handleConvert = async (quotationId: string) => {
    const salesRequestId = await handleConvertToSalesRequest(quotationId);
    if (salesRequestId) {
      // Navigate to the new sales request
      navigate(`/notas-de-venta/${salesRequestId}`);
    }
  };

  const handleGeneratePdf = async (quotation: (typeof quotations)[0]) => {
    setGeneratingPdfId(quotation.id);
    try {
      await generateQuotationPdf(quotation);
    } finally {
      setGeneratingPdfId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto space-y-4 py-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Presupuestos
              </CardTitle>
              <CardDescription>
                Lista de presupuestos creados. Para crear uno nuevo, ve al
                detalle del cliente.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {quotations.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <FileText className="mx-auto mb-4 h-12 w-12 opacity-50" />
              <p>No hay presupuestos</p>
              <p className="text-sm">
                Los presupuestos se crean desde el detalle del cliente
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>N°</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Válido hasta</TableHead>
                  <TableHead>Creado</TableHead>
                  <TableHead className="w-[100px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotations.map((quotation, index) => (
                  <TableRow
                    key={quotation.id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                  >
                    <TableCell className="font-medium">
                      #{String(quotation.quotation_number).padStart(4, "0")}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {quotation.client?.name ||
                            quotation.client?.company ||
                            "-"}
                        </div>
                        {quotation.client?.tax_id && (
                          <div className="text-xs text-muted-foreground">
                            {quotation.client.tax_id}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {formatCurrency(quotation.price_with_iva || 0)}
                    </TableCell>
                    <TableCell>
                      <QuotationStatusBadge status={quotation.status} />
                    </TableCell>
                    <TableCell>
                      <span
                        className={
                          dayjs(quotation.valid_until).isBefore(dayjs())
                            ? "text-red-600"
                            : ""
                        }
                      >
                        {dayjs(quotation.valid_until).format("DD/MM/YYYY")}
                      </span>
                    </TableCell>
                    <TableCell>
                      {dayjs(quotation.created_at).format("DD/MM/YYYY")}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link href={`/quotations/${quotation.id}`}>
                              <Eye className="mr-2 h-4 w-4" />
                              Ver detalle
                            </Link>
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onSelect={(e) => {
                              e.preventDefault();
                              void handleGeneratePdf(quotation);
                            }}
                            disabled={generatingPdfId === quotation.id}
                          >
                            {generatingPdfId === quotation.id ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <FileText className="mr-2 h-4 w-4" />
                            )}
                            {generatingPdfId === quotation.id
                              ? "Generando PDF..."
                              : "Generar PDF"}
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onClick={() => handleDuplicate(quotation.id)}
                          >
                            <Copy className="mr-2 h-4 w-4" />
                            Duplicar
                          </DropdownMenuItem>

                          <DropdownMenuSeparator />

                          {(quotation.status === "draft" ||
                            quotation.status === "sent" ||
                            quotation.status === "accepted") && (
                            <DropdownMenuItem
                              onClick={() => handleConvert(quotation.id)}
                            >
                              <ArrowRight className="mr-2 h-4 w-4" />
                              Convertir a Nota de Venta
                            </DropdownMenuItem>
                          )}

                          {canDeleteQuotation(quotation.status) && (
                            <>
                              <DropdownMenuSeparator />
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <DropdownMenuItem
                                    onSelect={(e) => e.preventDefault()}
                                    className="text-destructive"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Eliminar
                                  </DropdownMenuItem>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>
                                      ¿Eliminar presupuesto?
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Esta acción no se puede deshacer.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>
                                      Cancelar
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete(quotation.id)}
                                      className="bg-destructive text-destructive-foreground"
                                    >
                                      Eliminar
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PresupuestosPage;
